/**
 * Client telemetry — surface client-side failures we currently cannot see
 *
 * Nothing in this theme captured client-side errors before this file. Impulse's theme.js has no
 * `window.onerror` or `unhandledrejection` handler, and the only `error` listener in assets/ is a
 * single image fallback in bfx-video-gallery.js. So when a shopper hit a failure, it left no
 * trace, and the only way to learn about one was somebody describing it in chat.
 *
 * That is exactly how the 2026-08-20 "There was a problem loading this website" report went: the
 * origin measured completely healthy (every page type 200, TTFB 55–85ms, byte-identical
 * responses, zero Liquid errors, Shopify platform operational), so the failure had to be
 * client-side and time-dependent — but with no telemetry there was nothing to confirm it against.
 * The reported shape was ~7 storefront tabs all failing together after 30+ minutes idle, while
 * other sites in the same browser were fine, which points at per-tab resource exhaustion.
 *
 * ── What it captures ────────────────────────
 *
 * 1. Failed resource loads and uncaught JS errors — buffered by the inline bootstrap in
 *    snippets/bfx-client-telemetry-boot.liquid, because this asset loads `async` and so
 *    attaches its listeners well after anything that failed in the head.
 * 2. Failing app requests (cart, search, recommendations, products, collections) — via
 *    PerformanceObserver.
 * 3. Long tasks — main-thread blocking, which Shopify's own reporting does not cover.
 * 4. JS heap growth — the direct instrument for the idle-tab hypothesis above.
 *
 * ⚠️ Deliberately passive. An earlier draft wrapped `window.fetch` to catch failing cart calls,
 * which is the usual RUM approach and was rejected: a bug in a telemetry wrapper would break
 * add-to-cart on the live store. PerformanceResourceTiming.responseStatus gives the same signal
 * with no way to affect the request. Where it is unsupported we lose that one signal and
 * everything else still reports — never worth the risk of sitting in the request path.
 *
 * ── Not the regression guard ────────────────
 *
 * scripts/drift-guard/ also watches the live storefront, and the two are easy to confuse.
 * It is synthetic: CI drives Playwright over both markets twice daily and after each push to
 * main, comparing against a committed baseline, which is how it catches things that change the
 * store without a PR — an editor save, an app block pulled in admin, a half-finished sync.
 *
 * This file is the opposite vantage point: real shoppers' browsers, continuously. Neither
 * replaces the other, and the idle-tab report above is the clearest example — a synthetic run
 * loads a page fresh, so it can never sit idle for 30 minutes across seven tabs, and the guard
 * measures no heap at all. Only real sessions can show that failure.
 *
 * ── Where it reports ────────────────────────
 *
 * `window.dataLayer` (GTM/Stape is already live and server-side tagged, so this needs no new
 * vendor) and a Mouseflow session tag, which is the useful half for a bug like the idle-tab one:
 * Mouseflow already records sessions, and a tag makes the failing session's replay findable
 * instead of hunting through recordings. A console channel is available for QA behind a flag.
 *
 * A third sink pushes to Datadog Browser Logs (`window.DD_LOGS`, loaded by
 * snippets/bfx-datadog-logs-boot.liquid). This is the corrected destination decision — see
 * docs/dev/architecture/client-telemetry.md §6 — after Datadog RUM turned out to auto-capture
 * most of what this file already collects, redacts and dedupes. Browser Logs captures nothing on
 * its own (`forwardErrorsToLogs` stays off), so it is a plain sink like the other two: it only
 * ever receives what `report()` already built.
 *
 * ⚠️ No PII leaves the page, and it is structural rather than best-effort. Arbitrary text is not
 * exported at all: an uncaught error or rejection reports its KIND plus its scrubbed source
 * location
 * (`TypeError @ /cdn/.../theme.js:1204`), never its message, and stack traces are dropped from the exported
 * payload entirely. Redaction still runs on everything, but it is a denylist and a denylist
 * cannot promise this alone — `Customer Jane Doe, 42 Main Street` matches no pattern.
 *
 * Two different URL policies, because the two carry different risk:
 *   - The visitor's own page exports as a route FAMILY from a closed set (`cart`, `products`,
 *     `other`, …), never a path. This is the one a shopper can steer — anyone can request
 *     `/pages/<anything>` and a 404 still reports — so no free text may leave through it.
 *   - A failing resource's location keeps its path only where the segments are Shopify's or this
 *     theme's own — never a name somebody typed. That is exactly the twelve exact paths in
 *     `APP_ENDPOINTS` (`/cart/add.js` names what failed), and a `/cdn/shop/t/` (theme) or
 *     `/cdn/shopifycloud/` (platform) path only as the source of a JS error, and only when the CDN
 *     served that path to the page as a script (2xx): `//# sourceURL` lets evaluated code name
 *     itself anything, and a <script> tag can name a file that never loads. A failed REQUEST under
 *     either prefix exports the prefix (`<origin>/cdn/shop/t`, `<origin>/cdn/shopifycloud`): an
 *     editor can request any name there, through `asset_url` or raw markup, and the 404 reports.
 *
 *     ⚠️ NOT all of `/cdn/`, and not the `cart`/`search`/`recommendations` subtrees. Both were
 *     the original rule and both were too broad: `/cdn/shop/files/`, `/cdn/shop/collections/`
 *     and `/cdn/shop/videos/` hold MERCHANT-uploaded filenames, and `/search/Jane-Doe` is an
 *     ordinary 404 whose handle is EDITOR-chosen. Everything else — including a content route —
 *     reduces to its family, `/account/*` reduces to the origin because Shopify puts one-time
 *     tokens in that PATH, and a non-http scheme gets one fixed label.
 *
 * A cross-origin failure never exports its host: a hostname is free-form text, and an editor can
 * embed an image or script from any host. It exports `third-party:<vendor>` from the closed set in
 * `VENDOR_DOMAINS`, or plain `third-party`. Which vendor broke is the whole diagnostic value of a
 * third-party failure, so the set is matched by registrable domain, not taken from the theme's
 * markup: Bazaarvoice, Mouseflow, Braze and GTM load dynamically, and a markup-derived list would
 * miss them. A vendor added to the storefront reports as `third-party` until it is added there.
 *
 * No form values, inputs or cookies are read. Storage is read exactly once, for the debug flag
 * (`bfx-telemetry-debug` in localStorage) — a developer's own opt-in, never exported.
 *
 * The redacted text and the stack are available in the debug console, which is local and opt-in —
 * that is where a developer reads what happened, rather than a third-party sink. Both are bounded
 * there too, but at `DEBUG_MAX_STRING` rather than the export cap, so a stack keeps its frames.
 *
 * ── Lifecycle ───────────────────────────────
 *
 * Listeners are registered against an `AbortController` signal and the PerformanceObservers are
 * tracked, so `pagehide` tears both down — but only when `event.persisted` is false. A BFCache
 * hide is not the end of the page, and tearing down there would leave a restored session
 * reporting nothing.
 */
const initClientTelemetry = () => {
  const DATALAYER_EVENT = 'bfx_telemetry';

  /**
   * ⚠️ The dataLayer sink ships DISABLED, deliberately.
   *
   * If any tag in the GTM container fires on "All Events" or a loose Custom Event regex, a
   * telemetry report would trip it — worst case a GA4 or Meta conversion tag. That cannot be
   * verified from the theme side, because testing it means pushing a synthetic event into the
   * production dataLayer, which causes the exact harm being checked for.
   *
   * So it is off until someone with GTM access confirms no such tag exists.
   *
   * ⚠️ Mouseflow does NOT carry the signal in the meantime — it ships disabled too (see below).
   * Neither external sink reports until it is explicitly enabled. When the GTM audit is done this
   * is the smaller of the two to turn on, because Mouseflow cannot trip a GTM tag either way.
   *
   * To enable, after that check: set this to `true`, or set `window.__bfxTelemetryDataLayer = true`
   * before this script runs. No other change is needed.
   */
  const DATALAYER_ENABLED_BY_DEFAULT = false;

  const dataLayerEnabled = () =>
    window.__bfxTelemetryDataLayer === true || DATALAYER_ENABLED_BY_DEFAULT;

  /**
   * ⚠️ The Mouseflow sink ships DISABLED too, so the whole layer merges INERT.
   *
   * Nothing on this branch has been observed sending to a real vendor — the tests drive a stubbed
   * page, which cannot tell us what a live payload actually contains. Shipping inert separates the
   * two decisions: merging puts the code on production where it sends nothing, and enabling is a
   * one-line change made after reading real outbound payloads on an unpublished test theme.
   *
   * To enable, in that order: set this to `true`, or set `window.__bfxTelemetryMouseflow = true`
   * before this script runs. Turning it back off is the same one line, which is the rollback.
   */
  const MOUSEFLOW_ENABLED_BY_DEFAULT = false;

  const mouseflowEnabled = () =>
    window.__bfxTelemetryMouseflow === true || MOUSEFLOW_ENABLED_BY_DEFAULT;

  /**
   * ⚠️ The Datadog sink ships DISABLED too, for the same reason as Mouseflow above: shipping inert
   * separates "is the code correct" from "is it safe to send this to a third party" — the second
   * question needs a real payload read on an unpublished test theme, not a code review.
   *
   * To enable, after that check: turn on the theme's `datadog_telemetry_enabled` setting. That is
   * the whole answer — snippets/bfx-datadog-logs-boot.liquid sets `window.__bfxTelemetryDatadog`
   * inside the same guard that loads the SDK. Setting this constant or the window flag on their
   * own will not deliver anything: without the setting there is no `window.DD_LOGS` to attach to.
   */
  const DATADOG_ENABLED_BY_DEFAULT = false;

  const datadogEnabled = () => window.__bfxTelemetryDatadog === true || DATADOG_ENABLED_BY_DEFAULT;

  /**
   * Safeguard 2 — consent. Shopify's Customer Privacy API is the native gate; when it is present
   * and says analytics processing is not allowed, nothing is reported to any sink. When it is
   * absent we cannot ask, so dataLayer and Datadog (both require an explicit yes) stay closed and
   * Mouseflow remains the only sink — its own gate is the consent manager's decision to load it
   * at all, not this check.
   */
  const analyticsAllowed = () => {
    try {
      const api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.analyticsProcessingAllowed === 'function') {
        return api.analyticsProcessingAllowed();
      }
    } catch {
      // The API throws before it has loaded its state. Absence is not a denial, so fall through.
    }
    return null;
  };
  const MOUSEFLOW_TAG = 'bfx-telemetry';

  // A broken page can emit the same failure continuously. These caps keep a genuine incident from
  // turning into thousands of analytics hits, while still reporting enough to diagnose one.
  const MAX_REPORTS = 25;
  const MAX_STRING = 300;
  // The local debug console is not an export, so it gets a far larger bound — enough for a real
  // stack. Still bounded rather than unlimited: this file instruments a runaway page.
  const DEBUG_MAX_STRING = 4000;

  // Performance observations are the low-value half of this layer and by far the most numerous:
  // a long-lived tab can produce dozens of long tasks, each with a different duration and so a
  // different dedupe key. Without a separate budget they exhaust MAX_REPORTS and every later JS
  // error, failed cart request and heap-pressure event returns silently — the layer would go
  // quiet exactly when it matters. Failures always keep their own capacity.
  const OBSERVATION_TYPES = new Set(['long_task']);
  const MAX_OBSERVATION_REPORTS = 5;

  // ⚠️ A CIRCUIT BREAKER, not a lifetime ceiling — and the distinction is the whole point.
  //
  // This latched the layer into permanent silence three separate times, always the same shape: a
  // counter that only increases, checked BEFORE the thing that would reset it. Once it tripped,
  // every later report returned early without calling a sink, so the reset could never run. No
  // amount of recovery on the vendor's side brought reporting back.
  //
  // So it counts CONSECUTIVE refusals and trips, then lets one report through every
  // REFUSAL_PROBE_EVERY attempts. A recovered sink resets it on the next probe. Wasted payload
  // building stays bounded, and the bound heals itself.
  const MAX_CONSECUTIVE_REFUSALS = 20;
  const REFUSAL_PROBE_EVERY = 50;

  // A long task is 50ms by definition; 200ms is the point where it is a visible stall rather than
  // ordinary work.
  //
  // ⚠️ There is deliberately NO LCP observer here. Shopify's admin already reports LCP, INP and
  // CLS at P75 from real users (Online Store → Themes → the performance summary), across every
  // visitor rather than the budget-capped sample this layer can afford. Measuring LCP here
  // produced a second number that would disagree with the admin figure and answer no question the
  // admin does not answer better. Long tasks stay because main-thread blocking is NOT in that
  // report, and it is evidence for the idle-tab hypothesis this layer exists to test.
  const LONG_TASK_MS = 200;

  // Heap sampling for the idle-tab hypothesis: report when a tab's heap crosses this share of its
  // own limit, which is the condition that precedes a renderer being reclaimed.
  //
  // ⚠️ Two-phase on purpose. A flat 60s interval spent the whole 60-sample budget inside the first
  // hour, so the instrument went dark roughly an hour into a tab's life — and the report it was
  // built for was tabs failing after 30+ MINUTES idle, a range whose tail runs for hours. Sampling
  // every minute through the reported onset window and every ten after it stretches the same
  // 60-sample budget to ~5.5 hours without loosening the bound.
  const HEAP_FAST_MS = 60000;
  const HEAP_FAST_SAMPLES = 30;
  const HEAP_SLOW_MS = 600000;
  const HEAP_MAX_SAMPLES = 60;
  const HEAP_REPORT_RATIO = 0.7;

  // Same-origin paths worth flagging on a bad status — the requests that carry a shopper's
  // journey. A 404 on a stray image is noise; a 500 on /cart/add is the whole story.
  // Matched as path segments, for the same locale reason as `isAccountPath`: `/fr-ca/cart/add.js`
  // and `/fr/search` both put the route in the second segment.
  const APP_SEGMENTS = new Set(['cart', 'search', 'recommendations', 'products', 'collections']);

  const debugEnabled = () => {
    try {
      if (localStorage.getItem('bfx-telemetry-debug') === '1') return true;
    } catch {
      // Storage access throws in some privacy modes. The URL flag below still works, and a
      // telemetry layer that cannot read a debug preference must not itself become an error.
    }
    // ⚠️ `has()` is the wrong test: it is true for `=0`, `=false` and a bare flag with no value,
    // so `?bfx_telemetry_debug=0` turned the channel ON. The channel prints details and stacks
    // that are deliberately never exported, so it takes the same explicit `1` as the storage
    // switch above rather than merely the presence of the parameter.
    return new URLSearchParams(location.search).get('bfx_telemetry_debug') === '1';
  };

  const debug = debugEnabled();
  let reported = 0;
  let observationsReported = 0;
  let consecutiveRefusals = 0;
  let sinceProbe = 0;
  // ⚠️ TWO sets, because "already printed" and "already sent" are different questions.
  //
  // One shared set meant a console-only report during the documented debug-before-enable flow
  // marked the failure seen, so when a sink was finally enabled that same failure was deduped and
  // never left the browser — the debug channel silently consuming the signal it exists to surface.
  const egressedKeys = new Set(); // left the browser — the real dedupe
  const printedKeys = new Set(); // reached the console — bounds console noise only
  const controller = new AbortController();
  const { signal } = controller;
  const observers = [];
  const timerStops = [];

  // ── Payload hygiene ───────────────────────

  // Stacks are bounded, but at the DEBUG cap rather than the export cap. `MAX_STRING` exists to
  // keep an exported dataLayer payload small, and a stack is never exported (`report()` drops
  // `telemetry_stack` before the payload is built) — so capping it at 300 protected nothing and
  // cost the debug console most of its frames, which is the one place a developer reads it.
  // ⚠️ A rejection reason can be ANY value, and reading it can THROW.
  // `String(Object.create(null))` raises a TypeError, and `name`/`message`/`stack` may be
  // throwing getters. This runs inside the global rejection listener, so an unguarded read makes
  // the telemetry layer manufacture its own uncaught error and lose the original signal — the
  // same class of fault as an unisolated sink, from a different direction.
  const asText = (value) => {
    try {
      const t = typeof value;
      if (t === 'string') return value;
      if (t === 'number' || t === 'boolean') return String(value);
      return '';
    } catch {
      return '';
    }
  };

  const fieldOf = (holder, key) => {
    try {
      return asText(holder[key]);
    } catch {
      return '';
    }
  };

  const trimStack = (value) => String(value == null ? '' : value).slice(0, DEBUG_MAX_STRING);

  // Truncation is not redaction. `detail` can be an arbitrary rejection value and the stack
  // fields are raw traces, both of which routinely carry query-bearing URLs, addresses or
  // tokens — so the "no PII" property has to be enforced here rather than asserted in a comment.
  // Applied centrally in `report()`, which is what makes it structural: a new call site cannot
  // forget it.
  const EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
  const URL_IN_TEXT = /\bhttps?:\/\/[^\s'"()<>]+/g;
  // 20+ chars of base64/hex alphabet: session ids, JWT segments, API keys.
  const TOKEN = /\b[A-Za-z0-9_-]{20,}\b/g;
  // 7+ consecutive digits: order numbers, phone numbers, card fragments.
  const LONG_DIGITS = /\d{7,}/g;

  const redact = (value) =>
    String(value == null ? '' : value)
      .replace(EMAIL, '[email]')
      // Keep the origin + pathname so a stack frame still says which file failed.
      .replace(URL_IN_TEXT, (m) => {
        try {
          const u = new URL(m);
          return `${u.origin}${u.pathname}`;
        } catch {
          return m.split('?')[0];
        }
      })
      .replace(TOKEN, '[token]')
      .replace(LONG_DIGITS, '[number]');

  const safe = (value) => redact(value).slice(0, MAX_STRING);
  // Same redaction, debug bound. Redaction still runs even though this never leaves the browser:
  // "redaction runs on every string" is easier to keep true than to keep excepted.
  const safeDebug = (value) => redact(value).slice(0, DEBUG_MAX_STRING);

  // ⚠️ Redaction is a denylist, and a denylist cannot promise "no PII". `Customer Jane Doe, 42
  // Main Street` matches none of the patterns above. So arbitrary text does not leave the page at
  // all — see `groupingId` below for what is exported in its place.
  // Types whose detail is an arbitrary string rather than something we constructed.
  const FREE_TEXT_TYPES = new Set(['js_error', 'promise_rejection']);

  // A FIXED allowlist of built-in error names — never a pattern. `/[A-Za-z]{0,40}Error/` happily
  // accepts `JaneDoeError`, which would export shopper-controlled text under the guarantee that
  // says it never does. Anything not on this list is `unknown`.
  const ERROR_NAMES = new Set([
    'Error',
    'EvalError',
    'RangeError',
    'ReferenceError',
    'SyntaxError',
    'TypeError',
    'URIError',
    'AggregateError',
    'DOMException',
    'AbortError',
    'NotAllowedError',
    'NetworkError',
    'SecurityError',
    'TimeoutError',
    'QuotaExceededError',
  ]);

  /**
   * The error kind, from an explicit `name` when we have one and otherwise from the leading token
   * of the message. Either way it must be on the allowlist to be exported.
   */
  const errorKind = (name, message) => {
    if (name && ERROR_NAMES.has(name)) return name;
    const leading = String(message == null ? '' : message).match(/^\s*([A-Za-z]+)\b/);
    return leading && ERROR_NAMES.has(leading[1]) ? leading[1] : 'unknown';
  };

  /**
   * What gets exported in place of arbitrary text: allowlisted structural fields only.
   *
   * ⚠️ Deliberately NOT a hash of the message. A digest is derived from the personal data — that
   * is pseudonymised data, not redacted data — and a 32-bit digest of low-entropy text can be
   * enumerated back to the original. Error kind plus the already-scrubbed source location groups
   * failures just as usefully and carries nothing of the shopper's.
   */
  const groupingId = (type, detail, extra) => {
    if (!FREE_TEXT_TYPES.has(type)) return safe(detail);
    const where = extra.telemetry_source
      ? `${extra.telemetry_source}:${extra.telemetry_line || 0}`
      : 'no-source';
    // Through `safe()` as well. A script's pathname is not automatically harmless — it can carry
    // an email or a token — and `scrubUrl` only drops the query string. This branch would
    // otherwise duplicate that value into `telemetry_detail` unredacted and uncapped, which is
    // exactly the choke point everything else goes through.
    return safe(`${errorKind(extra.errorName, detail)} @ ${where}`);
  };

  // Origin + pathname only. Query strings are where identifiers and search terms live, so they
  // are dropped rather than sanitised — there is nothing in one we need to diagnose a failure.
  // Shopify embeds a one-time token directly in the path for /account/reset/{id}/{token} and
  // /account/activate/{id}/{token}, so origin + pathname is NOT safe there — dropping the query
  // string does nothing for a token that never was in the query.
  //
  // Matched on the path SEGMENT rather than a prefix built from `Shopify.routes.root`, because
  // that root reports `/fr` on the French-CA storefront while the real subfolder is `/fr-ca` —
  // `assets/bfx-language-selector.js` documents the same mismatch. Segment matching is correct
  // for every locale without needing to know any of them.
  const isAccountPath = (pathname) => {
    const segments = pathname.split('/').filter(Boolean);
    return segments[0] === 'account' || segments[1] === 'account';
  };

  // Ordinary storefront routes and theme assets only: `/collections/treadmills`,
  // `/cdn/shop/t/6/assets/custom.css`. No `%`, no `@`, no spaces — the characters an identifier
  // would need to survive in a path.
  const SAFE_PATHNAME = /^[A-Za-z0-9/._~-]*$/;

  // A tag name is markup an author controls, so it is free-form text like any other:
  // `<customer-jane-doe>` is a perfectly valid custom element. Allowlisted to the tags that
  // actually raise a resource `error`, plus the one custom element we know about by name.
  // Anything else reports as `custom-element` — enough to know the shape of the failure without
  // echoing whatever the element was called.
  const RESOURCE_TAGS = new Set([
    'script',
    'link',
    'img',
    'iframe',
    'video',
    'audio',
    'source',
    'track',
    'embed',
    'object',
    'shopify-account',
  ]);

  const safeTag = (tag) => {
    const lower = String(tag == null ? '' : tag).toLowerCase();
    return RESOURCE_TAGS.has(lower) ? lower : 'custom-element';
  };

  // Shopify's own route families, whose later segments are handles — public content, not customer
  // data. Anything outside them (an app proxy at /apps/**, a custom route) reduces to its origin,
  // because the first segment is exactly where a shopper's name could end up in a path.
  const KNOWN_ROUTES = new Set([
    'cdn',
    'collections',
    'products',
    'pages',
    'blogs',
    'cart',
    'search',
    'policies',
    'recommendations',
  ]);

  // The visitor's own page reports as a route FAMILY, never as a path.
  //
  // ⚠️ This is the field a shopper can steer. Anyone can request `/pages/<anything>` — a 404
  // still reports — so exporting the pathname meant `/pages/Jane-Doe-42-Main-Street` shipped
  // whole, through an allowlisted route, matching no redaction pattern. A denylist cannot fix
  // that; only a closed set of outputs can.
  //
  // Losing the path costs nothing we don't already have: this reporter tags the Mouseflow
  // session, so the exact URL is in the replay. Exporting it to dataLayer as well bought a
  // detail already on hand and spent the no-PII guarantee to get it.
  const ROUTE_FAMILIES = new Set([...KNOWN_ROUTES, 'account', 'checkout', 'apps']);

  // A localised storefront shifts every segment along by one (`/fr-ca/cart`), so the prefix comes
  // off once here and every rule below reads a single leading segment.
  //
  // ⚠️ Derived from `Shopify.locale`, NOT `Shopify.routes.root`. That is not a preference — it is
  // the same decision `assets/bfx-language-selector.js` already made, for the same reason,
  // recorded in `docs/dev/architecture/ca-language-selector.md`: `routes.root` reports `/fr`
  // while the published subfolder is `/fr-ca`, so trusting it would strip nothing and every CA
  // page would classify as `other` while CA app failures went unreported. `routes.root` is used
  // only as a fallback, for a future market whose prefix is not its locale.
  //
  // Matched on a full segment boundary — `/fr-ca` exactly or `/fr-ca/…` — so a handle that merely
  // begins with the locale (`/fr-ca-special`) is not mangled. Same guard as the selector's.
  const stripLocaleRoot = (pathname) => {
    const path = String(pathname == null ? '' : pathname);
    const lower = path.toLowerCase();

    const shopify = window.Shopify || {};
    const locale = String(shopify.locale || '').toLowerCase();
    const candidates = [];
    if (locale && locale !== 'en') candidates.push(`/${locale}`);
    const root = (shopify.routes && shopify.routes.root) || '/';
    if (root !== '/') candidates.push(root.replace(/\/$/, ''));

    for (const prefix of candidates) {
      if (lower === prefix || lower.startsWith(`${prefix}/`)) {
        return path.slice(prefix.length) || '/';
      }
    }
    return path;
  };

  // ⚠️ EXACT endpoint paths, not whole subtrees. Keeping everything under `cart`, `search` and
  // `recommendations` was too broad: an editor-authored `<img src="/search/Jane-Doe">` fires a
  // resource error and would have exported that handle in full. Only these fixed paths are
  // Shopify's own, so only these keep a path — which is all the signal needs, since its value is
  // knowing WHICH endpoint failed.
  // ⚠️ A resource-timing entry is produced for EVERY load, not just fetch/XHR. Without this an
  // `<img src="/search/Jane-Doe">` 404 matched the route check and was reported as a failed app
  // request — on top of the `resource_error` its own error event already produced. Two reports for
  // one failure, both spending the budget, in a file whose own comment says a 404 on a stray image
  // is noise and a 500 on /cart/add is the whole story.
  const REQUEST_INITIATORS = new Set(['fetch', 'xmlhttprequest']);

  const APP_ENDPOINTS = new Set([
    '/cart.js',
    '/cart/add.js',
    '/cart/update.js',
    '/cart/change.js',
    '/cart/clear.js',
    '/cart/shipping_rates.json',
    '/cart/prepare_shipping_rates.json',
    '/cart/async_shipping_rates.json',
    '/search/suggest',
    '/search/suggest.json',
    '/recommendations/products',
    '/recommendations/products.json',
  ]);

  // ⚠️ The last two browser-provided strings that were passed through RAW. Both are fixed enums in
  // every real browser, so neither is a live leak — but "structural, not best-effort" cannot have
  // two exceptions in it, and the file already allowlists error names and tag names for exactly
  // this reason. A canary sweep across every input surface is what surfaced them; a test per
  // known channel never would have.
  const INITIATOR_TYPES = new Set([
    'fetch',
    'xmlhttprequest',
    'script',
    'link',
    'img',
    'css',
    'beacon',
    'iframe',
    'video',
    'audio',
    'track',
    'embed',
    'object',
    'navigation',
    'other',
  ]);

  // PerformanceLongTaskTiming.name — the attribution bucket, not a task label.
  const LONG_TASK_NAMES = new Set([
    'self',
    'same-origin',
    'same-origin-ancestor',
    'same-origin-descendant',
    'cross-origin-ancestor',
    'cross-origin-descendant',
    'cross-origin-unreachable',
    'multiple-contexts',
    'unknown',
  ]);

  const safeEnum = (value, allowed, fallback) => {
    const lower = String(value == null ? '' : value).toLowerCase();
    return allowed.has(lower) ? lower : fallback;
  };

  // ⚠️ `/cdn/` is NOT uniformly Shopify's. Measured against the live storefront: `/cdn/shop/t/`
  // is theme assets and `/cdn/shopifycloud/` is platform scripts. `/cdn/shop/files/`,
  // `/cdn/shop/collections/` and `/cdn/shop/videos/` carry MERCHANT-UPLOADED filenames, so
  // `/cdn/shop/files/Jane-Doe.jpg` is author-controlled text and must reduce like any other handle.
  // ⚠️ And even the first two are prefixes, not proof of who chose the rest. An editor can REQUEST
  // any name under either (`asset_url`, or raw markup in a custom-content block), and the 404
  // still reports; a JS error's filename is whatever the throwing code calls itself, and
  // `//# sourceURL` lets evaluated code call itself anything. So a path under them keeps its name
  // only as the source of a JS error, and only when the page fetched exactly that path as a
  // script and the CDN served it (a 2xx; the CDN 404s a file that doesn't exist). A <script> tag
  // alone proves nothing: page code can add one for any name and throw before it fails.
  // Everything else exports the prefix itself, including in a browser that hides the status.
  const SERVED_CDN_PREFIXES = ['/cdn/shop/t/', '/cdn/shopifycloud/'];

  // An entry's `name` is always resolved, so a string compare needs no URL parse per entry.
  const isServedScript = (href) =>
    typeof performance.getEntriesByType === 'function' &&
    performance
      .getEntriesByType('resource')
      .some(
        (entry) =>
          entry.initiatorType === 'script' &&
          String(entry.name).split(/[?#]/)[0] === href &&
          entry.responseStatus >= 200 &&
          entry.responseStatus < 300
      );

  // ── Cross-origin hosts → vendor ids ─────────
  // A hostname is free text, and an editor can embed an image or script from any host, so a
  // cross-origin failure exports a fixed id from this closed set, never the host. A vendor's
  // registrable domain matches any of its subdomains, so vendors that load dynamically still
  // match. Measured on www.bowflex.com and www.bowflex.ca (2026-10-07), plus the theme's own
  // video and map embeds. Anything else exports `third-party` until it is added here.
  const THIRD_PARTY = 'third-party';
  const VENDOR_DOMAINS = new Map([
    ['shopify.com', 'shopify'],
    ['shopifycdn.com', 'shopify'],
    ['shopifysvc.com', 'shopify'],
    ['shop.app', 'shopify'],
    ['googletagmanager.com', 'google-tag-manager'],
    ['google.com', 'google'],
    ['doubleclick.net', 'google'],
    ['googleapis.com', 'google'],
    ['gstatic.com', 'google'],
    ['bazaarvoice.com', 'bazaarvoice'],
    ['braze.com', 'braze'],
    ['appboycdn.com', 'braze'],
    ['mouseflow.com', 'mouseflow'],
    ['datadoghq-browser-agent.com', 'datadog'],
    ['facebook.net', 'meta'],
    ['facebook.com', 'meta'],
    ['bing.com', 'microsoft'],
    ['clarity.ms', 'microsoft'],
    ['twitter.com', 'x'],
    ['ads-twitter.com', 'x'],
    ['t.co', 'x'],
    ['amazon-adsystem.com', 'amazon-ads'],
    ['adsrvr.org', 'trade-desk'],
    ['mountain.com', 'mntn'],
    ['config-security.com', 'triple-whale'],
    ['impactcdn.com', 'impact'],
    ['sjv.io', 'impact'],
    ['intelligems.io', 'intelligems'],
    ['consentmo.com', 'consentmo'],
    ['chargeafter.com', 'chargeafter'],
    ['fontawesome.com', 'fontawesome'],
    ['youtube.com', 'youtube'],
    ['youtube-nocookie.com', 'youtube'],
    ['ytimg.com', 'youtube'],
    ['vimeo.com', 'vimeo'],
    ['vimeocdn.com', 'vimeo'],
  ]);
  // Exact hosts only. Anyone can register under herokuapp.com, so matching its suffix would credit
  // a stranger's app to Impact; the `sst` hosts are this store's own server-side tagging.
  const VENDOR_HOSTS = new Map([
    ['saasler-impact.herokuapp.com', 'impact'],
    ['sst.bowflex.com', 'sst'],
    ['sst.bowflex.ca', 'sst'],
  ]);

  const vendorLabel = (hostname) => {
    const exact = VENDOR_HOSTS.get(hostname);
    if (exact) return `${THIRD_PARTY}:${exact}`;
    // Whole labels only, so `notbazaarvoice.com` and `bazaarvoice.com.example` never match.
    const labels = hostname.split('.');
    for (let i = 0; i < labels.length - 1; i += 1) {
      const vendor = VENDOR_DOMAINS.get(labels.slice(i).join('.'));
      if (vendor) return `${THIRD_PARTY}:${vendor}`;
    }
    return THIRD_PARTY;
  };

  const pageRoute = (pathname) => {
    const [first] = stripLocaleRoot(pathname).split('/').filter(Boolean);
    // No segment left after the locale root is the localised homepage (`/fr-ca/`), which is the
    // same page as `/` and must report the same family — it read as `other` before.
    if (!first) return 'home';
    const family = first.toLowerCase();
    return ROUTE_FAMILIES.has(family) ? family : 'other';
  };

  // `ranAsCode` is for the script a JS error was thrown from. Every other caller describes a
  // request, which is what the default assumes.
  const scrubUrl = (raw, { ranAsCode = false } = {}) => {
    if (!raw) return '';
    try {
      const url = new URL(raw, location.href);
      // Only http(s) may be exported. A `data:` URL carries the inline source **in its path**, so
      // origin + pathname would embed the script's own text in the report; `blob:` and
      // `javascript:` are the same shape of problem. Everything else gets an opaque label.
      // ONE fixed label, never the scheme itself. Schemes are free-form, so `customer-jane:` is a
      // valid URL and echoing the protocol would export arbitrary text through the very branch
      // that exists to stop it.
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '(non-http source)';
      // ⚠️ Before any path check, because each of those returns the origin, and a cross-origin
      // origin is a free-text host. A vendor id needs nothing from the path.
      if (url.origin !== location.origin) return vendorLabel(url.hostname);
      if (isAccountPath(url.pathname)) return url.origin;

      // ⚠️ Percent-encoding hides PII from the redaction pass: `/apps/proxy/jane%40example.com`
      // matches no pattern in `redact()`, and neither does `Jane%20Doe`. Decoding first would just
      // move the problem (and can throw on malformed input), so the shape has to be allowlisted.
      if (!SAFE_PATHNAME.test(url.pathname)) return url.origin;

      // ⚠️ But a shape check is only syntactic — `/apps/proxy/Jane-Doe/app.js` is all "safe"
      // characters. So the ROUTE has to be known too.

      const localPath = stripLocaleRoot(url.pathname);
      const [first] = localPath.split('/').filter(Boolean);
      if (!first) return url.origin;

      // ⚠️ A full path only where the segments are Shopify's or this theme's — never a name
      // somebody typed. A served `/cdn/shop/t/60/assets/theme.js` that threw and `/cart/add.js`
      // say what broke; a merchant's `/cdn/shop/files/Jane-Doe.jpg` and an editor's
      // `/pages/Jane-Doe` say who. A shape check cannot tell those apart, and `safe()` does not
      // redact a short name.
      if (first.toLowerCase() === 'cdn') {
        const prefix = SERVED_CDN_PREFIXES.find((candidate) => localPath.startsWith(candidate));
        if (!prefix) return `${url.origin}/cdn`;
        const served = ranAsCode && isServedScript(`${url.origin}${url.pathname}`);
        return served ? `${url.origin}${url.pathname}` : `${url.origin}${prefix.slice(0, -1)}`;
      }
      if (APP_ENDPOINTS.has(localPath.toLowerCase())) return `${url.origin}${url.pathname}`;

      return `${url.origin}/${pageRoute(url.pathname)}`;
    } catch {
      // Unresolvable (an inline handler, malformed markup). Do NOT echo the input back — that is
      // arbitrary page text, which is exactly what must not be exported.
      return '(unparseable source)';
    }
  };

  // A failed <script>/<link>/<img> is identified by its URL. A custom element fires `error` with
  // neither src nor href, so the TAG NAME ALONE is the whole report — `<shopify-account>` is how
  // the Login-with-Shop embed shows up, and that is enough to act on.
  //
  // ⚠️ The element id is deliberately NOT captured, even though it would disambiguate two
  // instances of the same tag. An id is free-form page data and can carry a customer name or
  // another identifier, and a resource error is not a free-text type — so an id would bypass the
  // no-PII protection entirely rather than being caught by it. Do not add it back.
  const describeElement = (tag, resource) => {
    const safe_tag = safeTag(tag);
    if (resource) return `${safe_tag}: ${scrubUrl(resource)}`;
    return safe_tag;
  };

  // ── Sinks ─────────────────────────────────

  const toDataLayer = (payload) => {
    // Created if absent so a report raised before GTM initialises is not lost — GTM drains any
    // queue it finds, which is the documented contract for this global. Safe to create here
    // because reaching this point already required the sink flag AND explicit analytics consent.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: DATALAYER_EVENT, ...payload });
      return true;
    } catch (error) {
      // ⚠️ GTM REPLACES `dataLayer.push` with its own function — measured on the live storefront,
      // where `dataLayer.push !== Array.prototype.push`. So this is third-party code executing
      // inside our own `error`/`unhandledrejection` listener, and a throw must be a refusal
      // rather than an escape. Escaping would skip the other sink, bypass the accounting, and
      // surface as a fresh uncaught error that this very listener would then report — a vendor
      // fault turning into a telemetry loop.
      if (debug)
        console.warn('[bfx-telemetry] the dataLayer sink threw; treated as a refusal', error);
      return false;
    }
  };

  const toMouseflow = (payload) => {
    if (!mouseflowEnabled()) return false;

    // ⚠️ Deliberately does NOT create `_mfq`. Mouseflow's loader preserves and drains an existing
    // queue when it eventually loads, so creating it here would smuggle commands captured while
    // consent was unknown out of the page the moment a shopper opts in — which bypasses the
    // loader-based gate this fallback claims to rely on.
    //
    // An `_mfq` that already exists IS the consent manager's signal that Mouseflow is allowed on
    // this page. No queue, no consent, no report.
    if (!Array.isArray(window._mfq)) return false;

    // The tag is what makes the session findable in the replay list; the variable carries enough
    // to know which failure the replay is showing without opening it.
    //
    // ⚠️ Two pushes, so PARTIAL delivery is possible: the tag can land and the variable throw.
    // Reporting that as a refusal would leave the report unaccounted — not marked seen, counted
    // against the breaker — so the next identical failure would push a SECOND tag and bypass the
    // egress budget. The tag alone is what makes the session findable in the replay list, which
    // is the load-bearing half, so once it lands this counts as delivered.
    let delivered = false;
    try {
      window._mfq.push(['tag', `${MOUSEFLOW_TAG}-${payload.telemetry_type}`]);
      delivered = true;
      window._mfq.push(['setVariable', 'bfxTelemetryDetail', payload.telemetry_detail]);
    } catch (error) {
      // Same isolation as the dataLayer sink: Mouseflow's loader also replaces `_mfq.push` with
      // its own drain, so one vendor throwing must not take the other sink or this listener down.
      if (debug) {
        console.warn(
          `[bfx-telemetry] the Mouseflow sink threw (tag ${delivered ? 'delivered' : 'not delivered'})`,
          error
        );
      }
    }
    return delivered;
  };

  const toDatadog = (payload) => {
    // ⚠️ Same isolation as the two sinks above: this runs inside our own error/unhandledrejection
    // listener, so a throw here — the SDK not being loaded yet, or a vendor bug — must be treated
    // as a refusal rather than allowed to escape and surface as a fresh uncaught error that this
    // very listener would then report.
    try {
      // Observations (long tasks) log at 'warn'; every other type here is a genuine failure.
      // Reuses OBSERVATION_TYPES rather than a second list, so the two cannot drift apart.
      const status = OBSERVATION_TYPES.has(payload.telemetry_type) ? 'warn' : 'error';
      window.DD_LOGS.logger.log(payload.telemetry_type, payload, status);
      return true;
    } catch (error) {
      if (debug)
        console.warn('[bfx-telemetry] the Datadog sink threw; treated as a refusal', error);
      return false;
    }
  };

  /**
   * ⚠️ Answers TWO questions, because the drain needs both and conflating them retained entries
   * forever. `egressed` is "did this leave the browser". `retriable` is "could a later attempt
   * still succeed" — false when an identical failure already went out or the page budget is
   * spent, true when the obstacle is temporary (no sink yet, consent not granted yet, breaker
   * open). Treating every `false` as retriable meant a duplicate buffered failure was deduped,
   * read as a refusal, and retried on every capture for the life of the page.
   */
  const DONE = { egressed: false, retriable: false };
  const LATER = { egressed: false, retriable: true };

  const dataLayerReady = () => dataLayerEnabled() && analyticsAllowed() === true;
  const mouseflowReady = () => mouseflowEnabled() && Array.isArray(window._mfq);
  // ⚠️ Requires the SAME explicit consent as the dataLayer sink, not Mouseflow's looser "does its
  // queue exist" test. Mouseflow's queue is only ever created by the consent manager, so its mere
  // presence IS the consent signal — but `window.DD_LOGS`'s existence is gated only by the
  // `datadog_telemetry_enabled` setting (snippets/bfx-datadog-logs-boot.liquid), not by consent, so
  // nothing else gates it. Without this check a shopper who declined analytics would still have
  // reports sent to Datadog.
  // `.logger` (rather than just `window.DD_LOGS`) is what distinguishes "the SDK loaded and
  // finished init()" from "only the CDN async stub has attached so far" — see the boot snippet.
  // ⚠️ `.logger.log` specifically, not just `.logger` — a partial init (or an SDK shape change)
  // could leave `logger` present without `.log`, and `toDatadog()` calls `.logger.log(...)`
  // directly. Checking only `.logger` would make this report ready, retry forever, and count every
  // attempt as a refusal toward the circuit breaker even though nothing could ever be sent.
  const datadogReady = () =>
    datadogEnabled() &&
    analyticsAllowed() === true &&
    !!(window.DD_LOGS && window.DD_LOGS.logger && typeof window.DD_LOGS.logger.log === 'function');
  /**
   * Could anything accept a report at this instant? `report()` asks exactly this before it does
   * any work, so a retry can ask it FIRST and skip the queue entirely rather than rebuilding
   * every retained entry only to have each one refused. Hoisted so the two cannot drift apart.
   */
  const anySinkReady = () => dataLayerReady() || mouseflowReady() || datadogReady() || debug;

  const report = (type, detail, extra = {}) => {
    // Consent is decided FIRST, before the dedupe set or either counter is touched. Deciding it
    // later meant 25 declined events could exhaust the page budget and mark every failure as
    // already-seen, so a shopper who later opted in would get silence.
    // ⚠️ TERMINAL, not retriable. Consent is a capture-time gate: a failure that happened while a
    // shopper had declined analytics must never be transmitted because they opted in afterwards.
    // Retaining it would turn consent into a question of when the queue drains rather than when
    // the failure occurred. A NEW failure after opt-in is unaffected — it is a fresh event, not
    // in `egressedKeys`, and reports normally.
    if (analyticsAllowed() === false) return DONE;

    // ⚠️ No sink available means nothing can be sent, so stop before building a payload and
    // before touching any counter. This used to fall through: with the dataLayer sink off by
    // default and `_mfq` absent until the consent manager loads Mouseflow, a failure repeating
    // 200 times exhausted the monotonic attempt ceiling and the layer then stayed silent for the
    // life of the page — including after Mouseflow finally loaded, which is the one moment it
    // needed to work. Availability is exactly knowable here (see the sinks above), so this costs
    // a boolean instead of a wasted build, and nothing is recorded as seen or counted.
    const dataLayerAvailable = dataLayerReady();
    const mouseflowAvailable = mouseflowReady();
    const datadogAvailable = datadogReady();
    // ⚠️ `debug` counts as a sink here. It is local and never leaves the browser, but it is the
    // channel a developer actually reads — and with all external sinks shipping off, gating on
    // them alone made `?bfx_telemetry_debug=1` print nothing but the startup line, which is the
    // opposite of what this file and the architecture guide promise.
    if (!dataLayerAvailable && !mouseflowAvailable && !datadogAvailable && !debug) return LATER;

    // ⚠️ The egress caps bound what LEAVES the page — they must not silence the local console.
    // Returning here meant that after 25 reports (or five long tasks) the debug channel went
    // quiet even though nothing was being sent, which is exactly when a developer most needs to
    // see what is happening. They gate delivery now, not the payload.
    const isObservation = OBSERVATION_TYPES.has(type);
    const withinBudget =
      reported < MAX_REPORTS && !(isObservation && observationsReported >= MAX_OBSERVATION_REPORTS);
    // Capped and nothing local to print: stop before building a payload nobody will read.
    if (!withinBudget && !debug) return DONE;

    // Dedupe on type + detail: a repeated identical failure adds no diagnostic value and would
    // consume the cap that a second, different failure needs.
    // Keyed on what is actually emitted, not the raw text. Two errors differing only in a query
    // token redact to the same payload, so keying on the raw detail would spend the budget
    // sending 25 identical reports.
    const emitted = groupingId(type, detail, extra);
    const key = `${type}|${emitted}`;
    // An identical failure already left the browser — nothing to retry.
    if (egressedKeys.has(key)) return DONE;

    // Attempts are counted HERE, after every quota and the dedupe — not before them. Counting
    // first meant 195 long tasks past the observation quota could exhaust the ceiling and drop a
    // later cart failure, which is the exact starvation the quota above exists to prevent. The
    // ceiling only needs to bound payload-building for reports that would otherwise proceed.
    // Breaker open: let a probe through periodically so a recovered sink can close it again.
    // Like the caps, it gates DELIVERY rather than the console — it only trips while sinks are
    // actively refusing, which is precisely when a developer wants to keep seeing reports.
    let breakerOpen = false;
    if (consecutiveRefusals >= MAX_CONSECUTIVE_REFUSALS) {
      sinceProbe += 1;
      if (sinceProbe < REFUSAL_PROBE_EVERY) breakerOpen = true;
      else sinceProbe = 0;
    }
    if (breakerOpen && !debug) return LATER;

    // Every string in the payload is redacted here, including whatever a call site put in
    // `extra` — stack traces above all. Non-strings are left alone; nothing sends objects.
    // Every string is redacted, and stacks are dropped from the exported payload entirely — a
    // stack is the single richest source of surprises and nothing downstream reads it.
    const redactedExtra = {};
    for (const [k, v] of Object.entries(extra)) {
      // `telemetry_stack` is never exported; `errorName` is internal routing for the grouping id.
      if (k === 'telemetry_stack' || k === 'errorName') continue;
      redactedExtra[k] = typeof v === 'string' ? safe(v) : v;
    }

    const payload = {
      telemetry_type: type,
      // Constructed details (a scrubbed URL, a status code, a duration) export as-is. Arbitrary
      // text exports as kind + fingerprint, never the text itself.
      telemetry_detail: emitted,
      // A route family from a closed set — not a URL. `safe()` is deliberately not applied:
      // there is no caller-controlled text here to redact, and wrapping it would imply the
      // value is untrusted when the point is that it cannot be steered at all.
      telemetry_page: pageRoute(location.pathname),
      telemetry_viewport: `${window.innerWidth}x${window.innerHeight}`,
      ...redactedExtra,
    };

    // Printed at most once per key. `egressedKeys` cannot serve here — a console-only report must
    // stay eligible to egress later, which is the whole point of keeping the two sets apart.
    if (debug && !printedKeys.has(key)) {
      printedKeys.add(key);
      // Deliberately console.warn, not console.log: in a QA session these need to stand out
      // against the theme's own logging, which is chatty.
      // The debug channel gets the redacted text and the stack at the DEBUG bound, not the
      // export bound — it never leaves the browser, and it is the only place a developer can read
      // what actually happened. Capping it at 300 like an exported string left roughly two frames.
      console.warn(`[bfx-telemetry] ${type}`, {
        ...payload,
        debug_detail: safeDebug(detail),
        debug_stack: safeDebug(extra.telemetry_stack || ''),
      });
    }

    // dataLayer needs an explicit yes. `null` means the Customer Privacy API is absent or not
    // ready, and enqueueing analytics before consent is *known* is not the same as knowing it is
    // allowed — so the documented Mouseflow-only fallback applies, whose own gate is whether the
    // consent manager loaded Mouseflow at all.
    // Delivery is gated here, after the payload exists and the console has seen it.
    const canEgress = withinBudget && !breakerOpen;
    const acceptedByDataLayer = canEgress && dataLayerAvailable ? toDataLayer(payload) : false;
    const acceptedByMouseflow = canEgress && mouseflowAvailable ? toMouseflow(payload) : false;
    const acceptedByDatadog = canEgress && datadogAvailable ? toDatadog(payload) : false;

    // Two different questions, and conflating them was a real bug found by running this against a
    // live storefront rather than a stub:
    //
    //   - did ANYTHING consume this?  → governs dedupe and the attempt ceiling
    //   - did it LEAVE THE BROWSER?   → governs the egress budgets and the return value
    //
    // The debug console consumes but does not egress. Treating "no external sink accepted" as
    // "nothing happened" meant that with debug on and every sink off — the shipped configuration —
    // `seen` was never written, so nothing deduped and every repeat of one failure climbed
    // the refusal count until the debug channel itself went silent. Measured on the real
    // storefront: eight reports, eight bogus "a sink refused" warnings, no dedupe.
    const consumed = acceptedByDataLayer || acceptedByMouseflow || acceptedByDatadog || debug;
    const egressed = acceptedByDataLayer || acceptedByMouseflow || acceptedByDatadog;

    // ⚠️ The breaker is accounted for BEFORE any early return. Putting it after the `!consumed`
    // exit made it unreachable in precisely the case it exists for — every sink available and
    // every sink refusing — so it never tripped and a broken vendor was retried on every failure.
    //
    // It tracks EGRESS only: a console-only report must not close it, or a persistently broken
    // sink would be retried on every failure whenever the debug flag is on.
    if (egressed) {
      consecutiveRefusals = 0;
      sinceProbe = 0;
    } else if (canEgress && (dataLayerAvailable || mouseflowAvailable || datadogAvailable)) {
      // Only a genuine attempt counts as a refusal — not one we declined to make.
      consecutiveRefusals += 1;
    }

    // Nothing consumed it at all — not even the console. Unreachable behind the availability gate,
    // which requires one of the three, and kept as a loud backstop for a future sink that can be
    // present and still refuse.
    if (!consumed) {
      if (debug) console.warn('[bfx-telemetry] nothing consumed the report', type);
      return LATER;
    }

    // Only what actually left is deduped against future sends, and only it spends the egress
    // budgets — which bound what LEAVES the page, not what a developer reads locally.
    if (egressed) {
      egressedKeys.add(key);
      reported += 1;
      if (isObservation) observationsReported += 1;
    }

    // Callers use this to know a report genuinely went out — the heap sampler must not retire on
    // a console-only report, or a debugging session would silence the signal it is looking at.
    // A console-only report stays retriable so a sink enabled later still receives it — unless
    // the page budget is spent, in which case no later attempt can ever succeed.
    if (egressed) return { egressed: true, retriable: false };
    return withinBudget ? LATER : DONE;
  };

  // ── 1. Drain what the bootstrap buffered ──

  const reportBufferedEntry = (entry) => {
    if (entry.type === 'resource_error') {
      return report('resource_error', describeElement(entry.tag, entry.resource), {
        telemetry_resource_tag: safeTag(entry.tag),
      });
    }
    if (entry.type === 'js_error') {
      return report('js_error', entry.message, {
        errorName: entry.name || '',
        telemetry_source: scrubUrl(entry.source, { ranAsCode: true }),
        telemetry_line: entry.line,
        telemetry_stack: trimStack(entry.stack),
      });
    }
    if (entry.type === 'promise_rejection') {
      return report('promise_rejection', entry.message, {
        errorName: entry.name || '',
        telemetry_stack: trimStack(entry.stack),
      });
    }
    // An entry shape this version does not know about cannot be reported, and retrying it forever
    // would pin the queue. Drop it.
    return DONE;
  };

  const drainBootQueue = () => {
    const boot = window.__bfxTelemetry;
    if (!boot || !Array.isArray(boot.queue)) return;

    // ⚠️ Keep what nothing accepted. Clearing unconditionally threw away exactly the failures the
    // bootstrap exists to preserve, and with every sink shipping disabled that is the DEFAULT
    // path, not an edge case: a failed theme.css captured in the head was built, refused and
    // deleted. Retained entries are retried by `retryBufferedEntries()` below.
    const unsent = boot.queue.filter((entry) => reportBufferedEntry(entry).retriable);
    boot.queue.length = 0;
    for (const entry of unsent) boot.queue.push(entry);

    // `ready` regardless: the live listeners are attached by now, so the bootstrap has no more
    // work and must stop buffering. Retention is about the entries already held, not about
    // continuing to collect.
    boot.ready = true;
  };

  // Retry on the next live capture rather than on a timer. By then a sink may have appeared — the
  // normal case, since the consent manager loads Mouseflow after this asset runs — and it costs
  // nothing on a page that never fails again. `report()` dedupes, so an entry that also arrived
  // live is not reported twice.
  // ⚠️ Live failures need the same retention as buffered ones. Retaining only the bootstrap queue
  // meant a one-off failure that happened AFTER handover but BEFORE a sink appeared was dropped
  // for good — and that window is the common case, not an edge one, because the consent manager
  // loads Mouseflow after this asset runs. The live listeners ignore `report()`'s result, so
  // nothing was holding those entries.
  //
  // Failures only. Observations (long tasks) are deliberately NOT retained: they are the
  // low-value half of this layer, they arrive in volume, and their own quota is five — holding
  // them would fill this queue with noise and crowd out the failures it exists for.
  const MAX_PENDING = 50;
  const pending = [];

  // How deep the pending queue is, exposed for debugging a live page — "is anything waiting on a
  // sink?" is otherwise unanswerable from the console. ⚠️ The COUNT only, never the array: these
  // entries hold raw pre-redaction detail, and handing that to any script on the page would
  // undo the guarantee this file exists to make. (The bootstrap's own queue is already visible
  // because the handshake requires it; this deliberately does not widen that.)
  const exposePendingCount = () => {
    const boot = window.__bfxTelemetry;
    if (!boot) return;
    try {
      Object.defineProperty(boot, 'pendingCount', {
        get: () => pending.length,
        configurable: true,
      });
      // Retained BYTES, which is the number that actually matters for a layer whose subject is
      // resource exhaustion — `pendingCount` bounds entries, this bounds what they hold. Also the
      // only way to observe the clamp: the export path truncates anyway, so a replayed payload
      // looks small whether or not the retained copy was bounded.
      Object.defineProperty(boot, 'pendingBytes', {
        get: () =>
          pending.reduce((total, item) => {
            let bytes = String(item.detail || '').length;
            for (const value of Object.values(item.extra || {})) {
              if (typeof value === 'string') bytes += value.length;
            }
            return total + bytes;
          }, 0),
        configurable: true,
      });
    } catch {
      // A frozen or otherwise hostile object is not worth failing over — it is a debug aid.
    }
  };

  const reportLive = (type, detail, extra) => {
    const outcome = report(type, detail, extra);
    if (!outcome.retriable || pending.length >= MAX_PENDING) return outcome;

    // ⚠️ Deduped before the capacity check. An unsent entry never reaches `egressedKeys`, so
    // fifty identical TypeErrors raised before Mouseflow loads would otherwise fill the queue and
    // crowd out a later distinct cart failure — while replaying them all would produce exactly
    // one report. Keyed the same way `report()` groups, so the queue holds one of each.
    const key = `${type}|${groupingId(type, detail, extra)}`;
    if (pending.some((item) => item.key === key)) return outcome;

    // ⚠️ And clamped. `MAX_PENDING` bounds the entry COUNT, not the bytes: `detail` is the raw
    // message and `telemetry_stack` the raw stack, so with the shipped sink-less default fifty
    // multi-megabyte rejections would be retained whole — exactly the unbounded retention the
    // bootstrap clamp exists to prevent, reintroduced one layer up.
    const clampedExtra = {};
    for (const [k, v] of Object.entries(extra || {})) {
      clampedExtra[k] = typeof v === 'string' ? v.slice(0, DEBUG_MAX_STRING) : v;
    }
    pending.push({
      key,
      type,
      detail: asText(detail).slice(0, DEBUG_MAX_STRING),
      extra: clampedExtra,
    });
    return outcome;
  };

  // ⚠️ Two drains with different costs, hooked to different triggers.
  //
  // NEITHER queue empties while a sink is absent — retention was added deliberately so a failure
  // caught in the head is not thrown away before anything can accept it, and with every sink
  // shipping disabled that is the DEFAULT state, not an edge case. So the boot queue is retried
  // on every live capture only because that is cheap once it is empty; while it is full, a page
  // failing in a loop would rebuild all 50 retained entries per failure. Measured before the
  // guard below: 100 live errors cost 5,200 URL parses instead of 100, a 52x amplification of
  // exactly the runaway this file exists to detect rather than cause.
  //
  // `anySinkReady()` is the fix, and it is the same question `report()` asks: if nothing could
  // accept a report, there is no reason to build one. The pending queue additionally gets only
  // the low-frequency triggers, which is all the "a sink appeared later" case needs.
  const retryBufferedEntries = () => {
    if (!anySinkReady()) return;
    const boot = window.__bfxTelemetry;
    if (!boot || !Array.isArray(boot.queue) || boot.queue.length === 0) return;
    const unsent = boot.queue.filter((entry) => reportBufferedEntry(entry).retriable);
    boot.queue.length = 0;
    for (const entry of unsent) boot.queue.push(entry);
  };

  const retryEverything = () => {
    if (!anySinkReady()) return;
    retryBufferedEntries();
    if (pending.length === 0) return;
    const stillPending = pending.filter(
      (item) => report(item.type, item.detail, item.extra).retriable
    );
    pending.length = 0;
    for (const item of stillPending) pending.push(item);
  };

  // ── 2. Live errors, once we are the listener ──

  // ⚠️ A retained head failure must not depend on a SECOND failure happening. The consent manager
  // typically loads Mouseflow after this asset runs, so the common shape is: the bootstrap catches
  // a lone stylesheet failure, the sink appears moments later, and nothing else ever fails. This
  // and the heap tick both give the queue a chance to drain without one.
  const watchVisibility = () => {
    document.addEventListener('visibilitychange', () => retryEverything(), { signal });
  };

  const watchLiveErrors = () => {
    window.addEventListener(
      'error',
      (event) => {
        retryBufferedEntries();
        const el = event.target;
        if (el && el !== window && el.tagName) {
          const tag = el.tagName.toLowerCase();
          // ⚠️ `currentSrc` before `src`: on a responsive <img>, `src` is the fallback the
          // browser may never have requested, so a srcset failure reported from `src` names a
          // URL that did not fail. And read every one through `fieldOf` — an SVG's `href` is an
          // SVGAnimatedString OBJECT, not a string, so an unguarded read hands `scrubUrl` a
          // non-string in a file whose own threat model assumes throwing getters.
          // `data` sits in the chain because <object> is an allowed resource tag and is the one
          // element that exposes its URL there rather than as `src` — without it an <object>
          // failure reported the tag name and lost the URL entirely. It goes LAST: <object> has
          // no `href`, so nothing is lost by ranking it below one, while an element carrying
          // both would otherwise report an unrelated `data` value instead of its real URL.
          const resource =
            fieldOf(el, 'currentSrc') ||
            fieldOf(el, 'src') ||
            fieldOf(el, 'href') ||
            fieldOf(el, 'data');
          reportLive('resource_error', describeElement(tag, resource), {
            telemetry_resource_tag: safeTag(tag),
          });
          return;
        }
        reportLive('js_error', asText(event.message) || 'unknown', {
          errorName: fieldOf(event.error, 'name'),
          telemetry_source: scrubUrl(asText(event.filename), { ranAsCode: true }),
          telemetry_line: event.lineno || 0,
          telemetry_stack: trimStack(fieldOf(event.error, 'stack')),
        });
      },
      { capture: true, signal }
    );

    window.addEventListener(
      'unhandledrejection',
      (event) => {
        retryBufferedEntries();
        const reason = event.reason;
        // Never `String(reason)`: that is the throwing case, and for an object it only produced
        // '[object Object]'. A primitive reason (`Promise.reject('boom')`) is used directly.
        reportLive('promise_rejection', fieldOf(reason, 'message') || asText(reason) || 'unknown', {
          // `name` is the kind: `new TypeError('boom').message` is only 'boom', so without this
          // every rejection on the page groups as `unknown` and collapses into one report.
          errorName: fieldOf(reason, 'name'),
          telemetry_stack: trimStack(fieldOf(reason, 'stack')),
        });
      },
      { signal }
    );
  };

  // ── 3. Failing app requests + perf anomalies ──

  const isAppRequest = (url) => {
    try {
      const parsed = new URL(url, location.href);
      if (parsed.origin !== location.origin) return false;
      // ⚠️ Strip the localised root instead of checking two segment positions. Matching the
      // second segment unconditionally reported `/pages/cart` and `/pages/search` as failed app
      // requests, because nothing verified the first segment was actually a locale — `cart` is
      // just as valid a page handle. `stripLocaleRoot()` owns that decision — locale-first, see
      // its comment for why `routes.root` alone is wrong here — so a
      // real `/fr-ca/cart/add.js` still matches and a content route no longer false-positives.
      // The extension strip lets `/cart.js` and `/cart/add.js` both match.
      const [segment] = stripLocaleRoot(parsed.pathname).split('/').filter(Boolean);
      if (!segment) return false;
      return APP_SEGMENTS.has(segment.replace(/\.(js|json)$/, ''));
    } catch {
      // An unparseable entry name cannot be matched to a route, so it is not an app request.
      return false;
    }
  };

  const observe = (type, handler, options = {}) => {
    if (typeof PerformanceObserver !== 'function') return;
    try {
      // ⚠️ Signature is `(entries, observer, options)` — `droppedEntriesCount` is on the THIRD
      // argument, and only on the first callback when buffered entries are replayed. Reading it
      // off the entry list silently yields `undefined`, and a test written from that same wrong
      // assumption passes while proving nothing. Confirmed against the spec, not inferred.
      const observer = new PerformanceObserver((list, _observer, options) => {
        // ⚠️ `buffered: true` replays what the browser recorded before this observer attached,
        // which is what makes loading the asset `async` safe — but the buffer is FINITE. The
        // resource-timing buffer holds 250 entries by default, and this storefront loads far more
        // than that, so on a heavy page early entries can be evicted before we attach. The spec
        // reports how many on the first callback; without this we were blind to being blind.
        //
        // Not reported to a sink: it is a property of the page's resource volume, not a failure,
        // and it would fire on every load. The debug channel is where it belongs.
        const dropped =
          options && typeof options.droppedEntriesCount === 'number'
            ? options.droppedEntriesCount
            : 0;
        if (dropped > 0 && debug) {
          console.warn(
            `[bfx-telemetry] ${dropped} buffered "${type}" entries were evicted before this observer attached`
          );
        }
        for (const entry of list.getEntries()) handler(entry);
      });
      observer.observe({ type, buffered: true, ...options });
      observers.push(observer);
    } catch {
      // An entry type this browser does not implement throws here. Losing one signal is
      // acceptable; letting it throw would take the rest of the telemetry down with it.
      if (debug) console.warn(`[bfx-telemetry] no support for PerformanceObserver type "${type}"`);
    }
  };

  const watchRequests = () => {
    observe('resource', (entry) => {
      // responseStatus is comparatively new. Where it is missing it reads as undefined, and the
      // comparison below is simply false — no signal rather than a wrong one.
      const status = entry.responseStatus;
      // Absence and zero are different answers. `undefined` means the browser does not implement
      // responseStatus, so there is no signal. `0` means the request failed at the network level —
      // a rejected cart call is exactly what this is for, and a falsy check dropped it.
      if (status === undefined || status === null) return;
      if (status !== 0 && status < 400) return;
      // Only a real request. A subresource load that happens to sit under an app route is not an
      // app request, and its failure is already reported by the `error` event.
      if (!REQUEST_INITIATORS.has(String(entry.initiatorType || '').toLowerCase())) return;
      if (!isAppRequest(entry.name)) return;
      reportLive(
        'request_failed',
        `${status === 0 ? 'network-error' : status} ${scrubUrl(entry.name)}`,
        {
          telemetry_status: status,
          telemetry_initiator: safeEnum(entry.initiatorType, INITIATOR_TYPES, 'other'),
        }
      );
    });
  };

  const watchPerformance = () => {
    observe('longtask', (entry) => {
      if (entry.duration < LONG_TASK_MS) return;
      // Bucketed to the nearest 50ms: an exact duration makes every task a unique key, which
      // defeats the dedupe and burns the observation budget on the same symptom.
      report(
        'long_task',
        `${Math.round(entry.duration / 50) * 50}ms ${safeEnum(entry.name, LONG_TASK_NAMES, 'unknown')}`,
        {
          telemetry_duration_ms: Math.round(entry.duration),
        }
      );
    });
  };

  // ── 4. Heap growth (the idle-tab instrument) ──

  const watchHeap = () => {
    // Non-standard and Chrome-only. Absent elsewhere, which is fine — the reported failure was on
    // Chrome, and this is a diagnostic rather than a guarantee.
    //
    // ⚠️ `performance.memory` is DEPRECATED and has no usable successor here. Chrome's replacement,
    // `performance.measureUserAgentSpecificMemory()`, requires cross-origin isolation (COOP+COEP)
    // — which a Shopify storefront can never enable without breaking every third-party embed it
    // depends on (Bazaarvoice, GTM, Mouseflow, shop.app, YouTube). So this signal works until
    // Chrome restricts it further and then stops, silently, with no migration path.
    //
    // That is a reason to keep the sampler cheap and not to build anything load-bearing on top of
    // it — not a reason to omit it, since nothing else can see per-tab heap growth and that is the
    // whole idle-tab hypothesis. The guard below means its removal degrades to silence, not an
    // error.
    if (!performance.memory) return;
    // ⚠️ Android WebView (the in-app browsers behind social and ad links, marked `; wv)` in the
    // UA) reports a heap pinned near its limit from the first sample, with identical figures
    // across unrelated sessions, so every WebView pageview reads as heap pressure. Removing this
    // turns the `heap_pressure` signal back into a WebView traffic counter.
    if (navigator.userAgent.includes('; wv)')) return;

    let samples = 0;
    let timer = 0;

    // ⚠️ An uncleared setInterval is the very thing this file is instrumenting
    // (assets/theme.js:6147), so this one is bounded three ways: a sample cap, a stop after it
    // reports, and `teardown()` clearing it on a real unload.
    const stopSampling = () => window.clearInterval(timer);
    // Handed to teardown rather than cleared from a `pagehide` listener of its own. That listener
    // was unreachable on a real unload, and the reason is the one people get wrong:
    //
    // ⚠️ `capture` orders listeners only when the node is an ANCESTOR of the target. `pagehide`
    // is dispatched AT `window`, and at-target both groups are invoked in a single
    // REGISTRATION-ORDER pass — the flag orders nothing there. Teardown registers first (linear
    // body execution, before `watchHeap()` runs), so its `controller.abort()` removed the
    // sampler's listener before dispatch ever reached it. Measured in Chrome 148: at-target on
    // `window`, a non-capture listener registered first runs BEFORE a capture listener
    // registered second; with `window` as a genuine ancestor the order flips.
    //
    // BFCache behaviour is unchanged, because teardown is guarded on `persisted` the same way.
    timerStops.push(stopSampling);

    const sample = () => {
      samples += 1;
      // A sink may have appeared since the handover, and a retained failure should not have to
      // wait for a second failure that may never come.
      //
      // ⚠️ This retry rides on the sampler's tick, so the backoff below slows it too: every
      // minute for the first HEAP_FAST_SAMPLES, then every ten. `watchVisibility` retries on
      // every visibilitychange and both error paths retry as well, so the only case that waits
      // the full ten minutes is a foregrounded tab sitting idle with nothing else failing.
      retryEverything();
      const { usedJSHeapSize, jsHeapSizeLimit } = performance.memory;
      const ratio = jsHeapSizeLimit ? usedJSHeapSize / jsHeapSizeLimit : 0;

      if (ratio >= HEAP_REPORT_RATIO) {
        // Only retire the sampler once a report actually went out. Consent may be denied right
        // now and granted later; clearing on a suppressed report would mean a shopper who opts in
        // can never produce the heap signal this whole sampler exists for.
        const sent = report('heap_pressure', `${Math.round(ratio * 100)}% of limit`, {
          telemetry_heap_used_mb: Math.round(usedJSHeapSize / 1048576),
          telemetry_heap_limit_mb: Math.round(jsHeapSizeLimit / 1048576),
          // Elapsed time since navigation, not samples x interval: a hidden tab throttles or
          // freezes the interval, so the sample count understates exactly the long-idle case
          // this signal exists to measure.
          telemetry_minutes_open: Math.round(performance.now() / 60000),
          telemetry_was_hidden: document.hidden ? 'yes' : 'no',
        });
        if (sent.egressed) {
          stopSampling();
          return;
        }
      }

      if (samples >= HEAP_MAX_SAMPLES) {
        stopSampling();
        return;
      }

      // Back off once the fast window is spent. Re-arming rather than running one slow interval
      // from the start keeps per-minute resolution across the reported 30-minute onset, where a
      // ten-minute gap could straddle the crossing this signal exists to catch.
      if (samples === HEAP_FAST_SAMPLES) {
        stopSampling();
        timer = window.setInterval(sample, HEAP_SLOW_MS);
      }
    };

    timer = window.setInterval(sample, HEAP_FAST_MS);
  };

  // ── Teardown ──────────────────────────────

  // The AbortController exists so the global listeners can be removed, and the observers have to
  // be disconnected by hand — neither happened before, so the "cleanup" was a claim rather than
  // a behaviour.
  //
  // ⚠️ Guarded on `event.persisted`. A BFCache hide is not the end of the page: tearing down
  // there would leave a restored session with no telemetry at all, which is the opposite of what
  // this is for. Only a real unload tears down.
  const teardown = () => {
    for (const observer of observers) {
      try {
        observer.disconnect();
      } catch {
        // An already-disconnected observer throws on some engines. There is nothing left to do
        // about it, and a telemetry teardown must not be the thing that throws on unload.
      }
    }
    observers.length = 0;
    for (const stop of timerStops) {
      try {
        stop();
      } catch {
        // Guarded for the same reason as the observer loop above, and it matters more here:
        // `controller.abort()` is the next statement, so a throw escaping this loop would skip it
        // and leave every listener attached on a real unload — the leak teardown exists to close.
      }
    }
    timerStops.length = 0;
    controller.abort();
  };

  window.addEventListener(
    'pagehide',
    (event) => {
      if (!event.persisted) teardown();
    },
    { signal }
  );

  // ── Wire up ───────────────────────────────

  // Listen before draining. Both calls run in the same tick, so there is no window an event could
  // arrive in — the ordering is not load-bearing the way an earlier version of this comment
  // claimed, and reversing it is undetectable by any test for that reason.
  //
  // What it actually buys is exception resilience, and the mechanism is worth stating correctly
  // because two earlier versions of this comment got it wrong. `boot.ready` is set at the END of
  // `drainBootQueue`, so a throw anywhere in it leaves `ready` false and the bootstrap still
  // buffering — it is never left inert-with-nothing-listening. The real difference:
  //
  //   - listen first, drain throws  → live listeners already attached, so reporting continues
  //   - drain first, drain throws   → `watchLiveErrors` never runs, so nothing reports at all
  //
  // An event seen by both halves is collapsed by the dedupe in `report()`, which is what makes
  // the overlap harmless.
  watchLiveErrors();
  drainBootQueue();
  watchRequests();
  watchPerformance();
  watchHeap();
  watchVisibility();
  exposePendingCount();

  if (debug) console.warn('[bfx-telemetry] active — debug channel on');
};

initClientTelemetry();
